import React, { useEffect, useRef, useState } from "react";
import { ActivityIndicator, FlatList, Text, View, ImageBackground, KeyboardAvoidingView, Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCrew } from "@/context/CrewProvider";
import { trpc } from "@/trpc/react";
import { MessageBubble } from "@/components/messages/message-bubble";
import { ChatInput } from "@/components/messages/chat-input";
import { useSupabase } from "@/hooks/useSupabase";
import * as FileSystem from 'expo-file-system/legacy';
import { decode } from 'base64-arraybuffer';
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

// Define attachment interface
interface Attachment {
    uri: string;
    name: string;
    type: string;
    size?: number;
}

export default function ConversationScreen() {
    const insets = useSafeAreaInsets();
    const params = useLocalSearchParams();
    const conversationId = typeof params.id === 'string' ? parseInt(params.id, 10) : 0;
    const { currentCrewMember } = useCrew();
    const { supabase } = useSupabase();
    const router = useRouter();
    const utils = trpc.useUtils();
    const flatListRef = useRef<FlatList>(null);
    const [isSending, setIsSending] = useState(false);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [isNearBottom, setIsNearBottom] = useState(true); // Track if user is at bottom of list

    // Debug Supabase
    useEffect(() => {
        if (supabase) {


            // Check if bucket exists
            const checkBucket = async () => {
                try {
                    const { data: buckets, error } = await supabase.storage.listBuckets();

                    if (error) {
                        console.error('Error listing buckets:', error);
                        return;
                    }



                    // Check if group-messages bucket exists
                    const bucketExists = buckets.some(b => b.name === 'group-messages');


                    if (!bucketExists) {

                        const { error: createError } = await supabase.storage.createBucket('group-messages', {
                            public: true
                        });

                        if (createError) {
                            console.error('Error creating bucket:', createError);
                        }
                    }
                } catch (e) {
                    console.error('Error checking buckets:', e);
                }
            };

            checkBucket();
        }
    }, [supabase]);

    // Get conversation details and messages
    const {
        data: conversation,
        isLoading,
        isError,
        refetch,
    } = trpc.messages.getMessagesForConversation.useQuery(
        {
            conversation_id: conversationId,
            crew_id: currentCrewMember?.id
        },
        {
            enabled: !!conversationId && conversationId > 0 && !!currentCrewMember?.id,
            refetchInterval: 15000, // Poll every 5 seconds
        }
    );

    // Send message mutation
    const { mutate: sendMessage } = trpc.messages.sendMessage.useMutation({
        onMutate: () => {
            setIsSending(true);
        },
        onSuccess: () => {
            refetch();
            setIsSending(false);
            // When a message is sent, we always want to scroll to bottom
            setIsNearBottom(true);
        },
        onError: () => {
            setIsSending(false);
        },
    });

    // Update the screen title when conversation data is loaded
    useEffect(() => {
        if (conversation?.conversation?.name) {
            // We can use Stack.Screen to update the header title dynamically
            // This will be handled in the layout component
        }
    }, [conversation]);

    // The getMessagesForConversation query has a server-side side effect: it
    // creates message_read_receipt rows for any unread messages in this
    // conversation. As soon as that fetch lands, the cached
    // getUnreadMessageCount used by the top banner and drawer indicator is
    // stale — invalidate it so they refresh without waiting for the 30s poll.
    useEffect(() => {
        if (!conversation || !currentCrewMember?.id) return;
        utils.messages.getUnreadMessageCount.invalidate({ crew_id: currentCrewMember.id });
    }, [conversation, currentCrewMember?.id, utils]);

    const isBroadcast = conversation?.conversation?.isBroadcast === true;

    // Add optimistic update for sent messages
    const [optimisticMessages, setOptimisticMessages] = useState<any[]>([]);

    // Combine server messages with optimistic messages
    const allMessages = [...(conversation?.messages || []), ...optimisticMessages];

    // Track previous message count to detect new messages
    const [previousMessageCount, setPreviousMessageCount] = useState(0);

    // Handle scroll events to detect if user is near bottom
    const handleScroll = (event: any) => {
        const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
        const paddingToBottom = 20; // How close to the bottom to consider "near bottom"

        // Calculate if the user is near the bottom of the list
        const isCloseToBottom =
            layoutMeasurement.height + contentOffset.y >=
            contentSize.height - paddingToBottom;

        setIsNearBottom(isCloseToBottom);
    };

    // Detect actual message changes to scroll to bottom even if user has scrolled up
    useEffect(() => {
        // Check if we actually have new messages (not just a re-render)
        if (conversation?.messages && conversation.messages.length > previousMessageCount) {
            // New messages received! Always scroll to show them
            if (flatListRef.current) {
                setTimeout(() => {
                    flatListRef.current?.scrollToEnd({ animated: true });
                }, 100);
            }
            // Update the previous count
            setPreviousMessageCount(conversation.messages.length);
        } else if (conversation?.messages) {
            // Just update the count if it's the first load
            setPreviousMessageCount(conversation.messages.length);
        }
    }, [conversation?.messages, previousMessageCount]);

    // Only scroll to bottom when user is already at the bottom or when sending messages
    useEffect(() => {
        if (flatListRef.current && allMessages?.length > 0 && isNearBottom) {
            setTimeout(() => {
                flatListRef.current?.scrollToEnd({ animated: true });
            }, 100);
        }
    }, [allMessages, isNearBottom]);

    const handleSendMessage = async (content: string, attachments?: Attachment[]) => {
        if (!currentCrewMember?.id || !conversationId) return;

        // Create an optimistic message
        const optimisticId = `temp-${Date.now()}`;
        const newOptimisticMessage = {
            id: optimisticId,
            content: content || 'Sent an attachment',
            createdAt: new Date(),
            senderType: "crew",
            senderId: currentCrewMember.id,
            senderName: currentCrewMember.name,
            attachments: [],
            isPending: true,
            readReceipts: []
        };

        // Add to optimistic messages
        setOptimisticMessages([...optimisticMessages, newOptimisticMessage]);

        // When sending a message, set isNearBottom to true to scroll to the bottom
        setIsNearBottom(true);

        // If there are attachments, we need to upload them first
        if (attachments && attachments.length > 0) {
            setUploadingAttachments(true);

            try {
                // Upload each attachment to Supabase storage
                const attachmentData = await Promise.all(
                    attachments.map(async (attachment) => {
                        // Create a temporary file path with a unique identifier
                        const tempFilePath = `tmp/${Date.now()}-${attachment.name}`;


                        try {
                            // Get file info to confirm it exists
                            const fileInfo = await FileSystem.getInfoAsync(attachment.uri);

                            if (!fileInfo.exists) {
                                throw new Error(`File does not exist at path: ${attachment.uri}`);
                            }



                            // Read the file as base64
                            const base64Content = await FileSystem.readAsStringAsync(attachment.uri, {
                                encoding: FileSystem.EncodingType.Base64
                            });

                            // Decode base64 to array buffer using base64-arraybuffer
                            const decodedContent = decode(base64Content);

                            // Upload to Supabase
                            const { data, error } = await supabase.storage
                                .from('group-messages')
                                .upload(tempFilePath, decodedContent, {
                                    contentType: attachment.type,
                                    upsert: true
                                });

                            if (error) {
                                console.error('Supabase upload error:', error);
                                throw new Error(`Failed to upload file: ${error.message}`);
                            }

                            // Return the attachment data
                            return {
                                file_path: tempFilePath,
                                fileName: attachment.name,
                                fileType: attachment.type,
                                fileSize: attachment.size || (base64Content.length * 0.75) // Estimate file size from base64 if not provided
                            };
                        } catch (uploadError) {
                            console.error('Error in file upload process:', uploadError);
                            throw uploadError;
                        }
                    })
                );

                // Send the message with attachment references
                sendMessage({
                    conversation_id: conversationId,
                    content: content || 'Sent an attachment',
                    crew_id: currentCrewMember.id,
                    attachments: attachmentData
                });

            } catch (error) {
                console.error('Error uploading attachments:', error);
                alert('Failed to upload attachments. Please try again.');
                // Remove the optimistic message on error
                setOptimisticMessages(optimisticMessages.filter(msg => msg.id !== optimisticId));
            } finally {
                setUploadingAttachments(false);
            }
        } else {
            // No attachments, just send the message
            sendMessage({
                conversation_id: conversationId,
                content,
                crew_id: currentCrewMember.id,
            });
        }
    };

    // Clear optimistic messages after successful refetch
    useEffect(() => {
        if (conversation?.messages) {
            setOptimisticMessages([]);
        }
    }, [conversation?.messages]);

    if (isLoading) {
        return (
            <View className="flex-1 justify-center items-center">
                <ActivityIndicator size="large" color="#0000ff" />
            </View>
        );
    }

    if (isError || !conversation) {
        return (
            <View className="flex-1 justify-center items-center p-4">
                <Text className="text-red-500 text-center mb-4">
                    Failed to load conversation
                </Text>
                <Text className="text-center text-gray-500">
                    Pull down to try again
                </Text>
            </View>
        );
    }

    return (
        <KeyboardAwareScrollView

            className="flex-1 bg-background"
            extraKeyboardSpace={Platform.OS === 'android' ? -12 : -24}

            contentContainerStyle={{
                flex: 1,

            }}
        >
            <View className="flex-1 bg-background">
                {uploadingAttachments && (
                    <View className="absolute top-0 left-0 right-0 bg-primary bg-opacity-90 py-2 z-10">
                        <Text className="text-white text-center">Uploading attachments...</Text>
                    </View>
                )}
                <ImageBackground
                    source={require("@/assets/images/crew-chat-bg-light.png")}
                    className="flex-1 bg-white"
                    resizeMode="repeat"
                    imageStyle={{
                        width: '200%',
                        height: '200%',
                    }}
                >
                    <FlatList
                        ref={flatListRef}
                        data={allMessages}
                        keyExtractor={(item) => item.id.toString()}
                        renderItem={({ item }) => (
                            <MessageBubble
                                content={item.content}
                                timestamp={item.createdAt}
                                isFromCurrentUser={
                                    item.senderType === "crew" &&
                                    item.senderId === currentCrewMember?.id
                                }
                                senderName={
                                    item.senderType === "crew" &&
                                        item.senderId !== currentCrewMember?.id
                                        ? item.senderName
                                        : item.senderType === "staff"
                                            ? item.senderName
                                            : undefined
                                }
                                senderType={item.senderType}
                                senderId={item.senderId}
                                senderAvatar={
                                    item.senderType === "staff" && item.senderId
                                        ? conversation?.participants?.find(
                                            p => p.type === "staff" && p.userId === item.senderId
                                        )?.avatar
                                        : undefined
                                }
                                attachments={item.attachments}
                                isPending={item.isPending}
                                readReceipts={item.readReceipts?.map((receipt: {
                                    id: number;
                                    readAt: Date;
                                    participantId: number;
                                }) => ({
                                    ...receipt,
                                    participantName: conversation?.participants?.find(
                                        p => p.id === receipt.participantId
                                    )?.name || undefined
                                }))}
                                allParticipants={conversation?.participants?.map(p => ({
                                    ...p,
                                    userId: p.userId ?? 0,
                                    avatar: p.type === "staff" ? p.avatar : undefined
                                }))}
                                currentUserId={currentCrewMember?.id}
                                isJobConversation={conversation?.conversation?.type === "JOB"}
                            />
                        )}
                        className="flex-1 p-4"
                        onRefresh={refetch}
                        refreshing={isLoading}
                        inverted={false}
                        contentContainerStyle={{ paddingBottom: 10 }}
                        onScroll={handleScroll}
                        scrollEventThrottle={400}
                    />
                </ImageBackground>
                <View style={{ paddingBottom: Platform.OS === 'android' ? insets.bottom + 12 : 24 }}>
                    {isBroadcast ? (
                        <View className="border-t border-gray-200 bg-gray-50 py-3 px-4">
                            <Text className="text-center text-gray-500 text-sm">
                                This is an announcement. Replies are not allowed.
                            </Text>
                        </View>
                    ) : (
                        <ChatInput
                            onSendMessage={handleSendMessage}
                            disabled={isSending || uploadingAttachments}
                        />
                    )}
                </View>
            </View>
        </KeyboardAwareScrollView>
    );
} 